import React, {useEffect, useRef, useState} from 'react';
import {selectStyles} from '../styles';
import {SelectOption} from '../types';

interface SelectProps {
  label: string;
  options: SelectOption[];
  value?: string | number;
  onChange: (value: any) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
}

/**
 * Dropdown de la maquette : libellé principal + texte secondaire optionnel,
 * options désactivables, coche sur l'option sélectionnée.
 */
export const Select = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Placeholder',
  required = false,
  disabled = false,
  emptyMessage = 'Aucune option disponible',
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onClickOutside = (event: any) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: any) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);

  const selectOption = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={selectStyles.container}>
      <label style={selectStyles.label}>
        {label}
        {required && <span style={selectStyles.required}>*</span>}
      </label>

      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
        style={{
          ...selectStyles.field,
          ...(disabled ? selectStyles.fieldDisabled : {}),
          ...(isOpen ? selectStyles.fieldOpen : {}),
        }}>
        <span
          style={{
            ...selectStyles.value,
            ...(selectedOption ? {} : selectStyles.placeholder),
          }}>
          {selectedOption ? selectedOption.label : placeholder}
          {selectedOption?.hint && (
            <span style={selectStyles.optionHint}> {selectedOption.hint}</span>
          )}
        </span>
        <i className="fa fa-angle-down" style={selectStyles.caret} />
      </button>

      {isOpen && (
        <ul role="listbox" style={selectStyles.menu}>
          {options.length === 0 && (
            <li style={selectStyles.emptyOption}>{emptyMessage}</li>
          )}
          {options.map(option => {
            const isSelected = option.value === value;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onClick={() => selectOption(option)}
                style={{
                  ...selectStyles.option,
                  ...(isSelected ? selectStyles.optionSelected : {}),
                  ...(option.disabled ? selectStyles.optionDisabled : {}),
                }}>
                <span>{option.label}</span>
                {option.hint && (
                  <span
                    style={
                      option.disabled
                        ? selectStyles.optionHintDisabled
                        : selectStyles.optionHint
                    }>
                    {option.hint}
                  </span>
                )}
                {isSelected && (
                  <i className="fa fa-check" style={selectStyles.optionCheck} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Select;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/components/Select.tsx